/*
 * <mix-match-sale> – Bundle-Builder „X % auf jede 2. Bowl“
 *
 * Rechnet nur die Vorschau. Den echten Rabatt zieht Discount Plus im Warenkorb ab.
 * Weicht die Vorschau vom Warenkorb ab, ist die Vorschau falsch (nicht umgekehrt).
 *
 * Paarung wie Discount Plus: alle Einheiten nach Preis absteigend (stabil nach
 * Auswahlreihenfolge), Paare 1+2, 3+4 …, die zweite Bowl jedes Paars ist reduziert.
 */
(() => {
  if (customElements.get('mix-match-sale')) return;

  const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
  const AUTO_OPEN_MS = 3200;

  class MixMatchSale extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const src = this.querySelector('[data-mm-data]');
      if (!src) return;

      const data = JSON.parse(src.textContent);
      this.cfg = data.config;

      // Aktionsende auch im Browser prüfen: gecachte Seite oder Seite über das Ende hinweg geöffnet
      const end = this.cfg.end ? new Date(this.cfg.end).getTime() : NaN;
      if (!isNaN(end) && end <= Date.now()) {
        this.classList.add('is-ended');
        return;
      }
      this.ready = true;
      if (!isNaN(end) && end - Date.now() < 864e5) {
        this.endTimer = setTimeout(() => this.showEnded(), end - Date.now());
      }

      this.pct = Number(this.cfg.pct) || 0;
      this.bowls = data.bowls;
      this.B = new Map(this.bowls.map((b) => [String(b.id), b]));
      this.T = new Map(data.toppings.map((t) => [String(t.id), t]));
      this.perks = (data.perks || []).filter((p) => p.min > 0).sort((a, b) => a.min - b.min);
      this.money = new Intl.NumberFormat(this.cfg.locale || 'de', { style: 'currency', currency: this.cfg.currency || 'EUR' });

      this.seq = [];
      this.uid = 0;
      this.topsOn = new Set();
      this.newUid = null;
      this.mode = { type: 'add' };
      this.lastFocus = null;
      this.prevReached = 0;
      this.lastNudged = null;
      this.userOpened = false;
      this.timer = null;

      this.el = {};
      this.querySelectorAll('[data-mm]').forEach((n) => (this.el[n.dataset.mm] = n));

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerdown = this.onPointerdown.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      // pointerdown statt click: sonst schließt der Klick, der das Auto-Aufklappen ausgelöst hat, das Panel sofort wieder
      document.addEventListener('pointerdown', this.onPointerdown);

      this.watchHeader();
      this.watchEnd();
      this.onMq = () => this.render();
      this.mqs = ['(min-width: 750px)', '(min-width: 990px)', '(min-width: 1200px)'].map((q) => window.matchMedia(q));
      this.mqs.forEach((m) => m.addEventListener('change', this.onMq));
      this.render();
      this.loadCart();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.loadCart());
      }
      this.onPromo = () => this.loadCart();
      window.addEventListener('herbst-promo:updated', this.onPromo);
    }

    disconnectedCallback() {
      if (!this.ready) return;
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerdown', this.onPointerdown);
      this.headerRo?.disconnect();
      this.headerMo?.disconnect();
      this.endIo?.disconnect();
      this.cartUnsub?.();
      this.mqs?.forEach((m) => m.removeEventListener('change', this.onMq));
      window.removeEventListener('herbst-promo:updated', this.onPromo);
      clearTimeout(this.timer);
      clearTimeout(this.endTimer);
      if (this.isSheetOpen()) document.body.classList.remove('overflow-hidden');
      this.ready = false;
    }

    showEnded() {
      clearTimeout(this.endTimer);
      if (this.isSheetOpen()) this.closeSheet();
      this.setBarOpen(false);
      this.classList.add('is-ended');
      window.scrollTo({ top: Math.max(0, this.getBoundingClientRect().top + window.scrollY - 120) });
    }

    // ---------- Helfer ----------
    fmt(c) {
      return this.money.format(c / 100);
    }
    // Schwellen ohne ,00 („ab 110 €“)
    fmtShort(c) {
      return c % 100 ? this.fmt(c) : this.fmt(c).replace(/[,.]00(?=\D|$)/, '');
    }
    // Discount Plus schneidet ab statt zu runden (gemessen: 30 % von 15,99 € = 4,79 €)
    off(p) {
      return Math.floor((p * this.pct) / 100);
    }
    bowl(id) {
      return this.B.get(String(id));
    }
    // Spalten der Paar-Grid (je nach Breakpoint 1 oder 2)
    cols() {
      const t = this.el.pairs ? getComputedStyle(this.el.pairs).gridTemplateColumns : '';
      return t && t !== 'none' ? Math.max(1, t.split(' ').filter(Boolean).length) : 1;
    }
    // Anfangs 2 Reihen; liegt in einer Reihe eine Bowl, erscheint die nächste
    pairCount(n) {
      const cols = this.cols();
      const used = Math.ceil(n / 2);
      const lastRow = used ? Math.floor((used - 1) / cols) : -1;
      return Math.max(2, lastRow + 2) * cols;
    }
    isSheetOpen() {
      return !this.el.overlay.hidden;
    }

    // Grundpreis (PAngV). Bevorzugt unit_price_measurement aus Shopify.
    unitText(b) {
      let qty = b.qty;
      let unit = b.qtyUnit;
      let up = b.unitPrice;
      let ref = b.refValue;
      let refUnit = b.refUnit;
      if (!up || !qty) {
        // Lücke: Die Variante hat kein unit_price_measurement gepflegt. Fallback: Füllmenge aus dem
        // Variantentitel („1x 400g“) lesen und auf 1 kg bzw. 1 l rechnen. variant.weight taugt dafür
        // nicht, das ist das Versandgewicht inkl. Verpackung. Besser: Grundpreis im Admin pflegen.
        const m = /(\d+(?:[.,]\d+)?)\s*(kg|g|ml|l)\b/i.exec(`${b.variantTitle} ${b.title}`);
        if (!m) return '';
        qty = parseFloat(m[1].replace(',', '.'));
        unit = m[2].toLowerCase();
        const base = unit === 'kg' || unit === 'l' ? qty : qty / 1000;
        up = Math.round(b.price / base);
        ref = 1;
        refUnit = unit === 'ml' || unit === 'l' ? 'l' : 'kg';
      }
      const refLabel = (Number(ref) === 1 ? '' : `${ref} `) + refUnit;
      return `${qty} ${unit}, ${this.fmt(up)}/${refLabel}`;
    }

    // ---------- Rechnung ----------
    // Discount Plus reduziert die günstigste Hälfte (gemessen am 2026-09-29).
    // Anzeige-Reihenfolge: teuerste + günstigste, zweitteuerste + zweitgünstigste … → in jedem Paar
    // ist genau die zweite (günstigere) Bowl reduziert; bei ungerader Anzahl wartet die mittlere auf ihren Partner.
    ordered() {
      const d = this.seq
        .map((e, i) => ({ ...e, i }))
        .sort((a, b) => this.bowl(b.id).price - this.bowl(a.id).price || a.i - b.i);
      const n = d.length;
      const k = Math.floor(n / 2);
      const o = [];
      for (let i = 0; i < k; i++) o.push(d[i], d[n - 1 - i]);
      if (n % 2) o.push(d[k]);
      return o;
    }

    calc() {
      const o = this.ordered();
      const bowlSum = o.reduce((a, e) => a + this.bowl(e.id).price, 0);
      const topSum = [...this.topsOn].reduce((a, id) => a + this.T.get(id).price, 0);
      let pot = 0;
      o.forEach((e, i) => {
        if (i % 2) pot += this.off(this.bowl(e.id).price);
      });
      const gross = bowlSum + topSum;
      // Mindestbestellwert gilt auf den gesamten Warenkorb (vor Rabatt): was schon drin liegt, zählt mit
      const inCart = this.cartGross || 0;
      const ok = gross + inCart >= this.cfg.min && o.length >= 2;
      const minGap = Math.max(0, this.cfg.min - gross - inCart);
      const disc = ok ? pot : 0;
      return { o, count: o.length, bowlSum, topSum, pot, gross, inCart, minGap, ok, disc, total: gross - disc };
    }

    // Gratis-Stufen (Versand + Geschenke), Basis = total wie beim Gratis-Versand.
    // Nur die nächste Stufe bekommt „Noch X €“, und nur wenn sie in Reichweite liegt:
    // ferne Ziele (Median-Warenkorb ~54 €, 90 € erreichen ~10 %) wirken eher entmutigend.
    perkState(r) {
      // Basis: Bundle nach Rabatt + was schon im Warenkorb liegt (wie Gratis-Versand im Checkout)
      const base = r.total + (this.cartNet || 0);
      const active = base > 0;
      const next = this.perks.find((p) => base < p.min) || null;
      const gap = next ? next.min - base : 0;
      const near = !!next && active && gap <= this.cfg.reach;
      return { next, gap, near, base, active };
    }

    renderPerks(r) {
      const ps = this.perkState(r);
      // Hero-Chips: erreichte Stufen grün markieren
      this.querySelectorAll('[data-mm-perk-min]').forEach((li) => {
        li.classList.toggle('is-done', ps.active && ps.base >= Number(li.dataset.mmPerkMin));
      });
      this.el.perks.innerHTML = this.perks
        .map((p) => {
          const done = ps.active && ps.base >= p.min;
          const isNext = ps.near && p === ps.next;
          const cls = done ? ' is-done' : isNext ? ' is-next' : '';
          const sub = done ? 'Ist dabei' : isNext ? `Noch ${this.fmt(ps.gap)}` : `ab ${this.fmtShort(p.min)}`;
          const ic = done
            ? '<span class="mm-perk__ic mm-perk__ic--done" aria-hidden="true">✓</span>'
            : p.img
              ? `<img class="mm-perk__ic" src="${esc(p.img)}" alt="" width="32" height="32" loading="lazy">`
              : `<span class="mm-perk__ic" aria-hidden="true">${p.ship ? '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.6"/><circle cx="17" cy="17.5" r="1.6"/></svg>' : '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-1.5-3-5-3-5-1s3 1 5 1c2 0 5 1 5-1s-3.5-2-5 1"/></svg>'}</span>`;
          return `<li class="mm-perk${cls}">${ic}<span class="mm-perk__t">${esc(p.title)}</span><span class="mm-perk__s">${sub}</span></li>`;
        })
        .join('');
      return ps;
    }

    // ---------- Belohnungsleiste: nur die nächste −X %-Bowl ----------
    reward(r) {
      const p = this.pct;
      if (!r.count) return { html: `Wähle 2 Bowls, <em>jede 2. ist ${p} % günstiger</em>` };
      if (r.count === 1) return { html: `Noch 1 Bowl bis zur <em>−${p} %-Bowl</em>`, nudge: true };
      if (r.count % 2) return { html: `Deine nächste Bowl ist <em>${p} % günstiger</em>`, nudge: true };
      if (!r.ok) {
        const n = r.count / 2;
        return {
          html: `${n} ${n === 1 ? 'Paar' : 'Paare'} komplett, <em>Rabatt gilt ab ${esc(this.cfg.minLabel)}</em> (noch ${this.fmt(r.minGap)})`,
        };
      }
      // Rabatt aktiv, gerade Anzahl: die nächste −X % ist 2 Bowls (~25–30 €) entfernt.
      // Liegt eine Gratis-Stufe in Reichweite, ist sie das nähere Ziel → die zeigen (abschaltbar).
      if (this.cfg.barPerks) {
        const ps = this.perkState(r);
        if (ps.near) return { html: `Du sparst ${this.fmt(r.disc)}. <em>Noch ${this.fmt(ps.gap)} bis ${esc(ps.next.title)}</em>` };
      }
      return { html: `Du sparst <em>${this.fmt(r.disc)}</em>. Noch 2 Bowls bis zur nächsten −${p} %` };
    }

    renderBar(r) {
      const st = this.reward(r);
      const n = this.pairCount(r.count);
      if (this.el.rbMsg.innerHTML !== st.html) this.el.rbMsg.innerHTML = st.html;

      let track = '';
      let list = '';
      for (let p = 0; p < n; p++) {
        const a = r.o[p * 2];
        const b = r.o[p * 2 + 1];
        const full = a && b;
        const segCls = full ? (r.ok ? ' mm-seg--done' : ' mm-seg--wait') : '';
        track += `<span class="mm-seg${segCls}"><i class="${a ? 'is-on' : ''}"></i><i class="${b ? 'is-on' : ''}"></i></span>`;

        let cls = '';
        let ic = `−${this.pct}%`;
        let head = `Paar ${p + 1}`;
        let sub = 'Noch 2 Bowls';
        if (full) {
          const save = this.fmt(this.off(this.bowl(b.id).price));
          cls = r.ok ? 'is-done' : 'is-wait';
          if (r.ok) ic = '✓';
          head = `${esc(this.bowl(a.id).title.split(' ')[0])} + ${esc(this.bowl(b.id).title.split(' ')[0])}`;
          sub = r.ok ? `−${save} gespart` : `−${save} ab ${esc(this.cfg.minLabel)}`;
        } else if (a) {
          cls = 'is-cur';
          sub = 'Noch 1 Bowl';
        }
        list += `<li class="mm-pl__item ${cls}"><span class="mm-pl__ic" aria-hidden="true">${ic}</span><span><b>${head}</b><small>${sub}</small></span></li>`;
      }
      this.el.rbTrack.innerHTML = track;
      this.el.rbList.innerHTML = list;
      this.el.rb.classList.toggle('mm-rb--nudge', !!st.nudge);
      this.el.rbGo.hidden = !st.nudge;

      // Auto-Aufklappen nur bei geschlossenem Sheet; Änderungen im Sheet werden beim Schließen ausgewertet
      if (this.isSheetOpen()) return;
      const pairsDone = Math.floor(r.count / 2);
      if (st.nudge && r.count !== this.lastNudged) {
        this.lastNudged = r.count;
        this.el.rb.classList.remove('mm-rb--pulse');
        void this.el.rb.offsetWidth;
        this.el.rb.classList.add('mm-rb--pulse');
        this.autoOpen();
      } else if (pairsDone > this.prevReached) {
        this.autoOpen();
      }
      if (!st.nudge) this.lastNudged = null;
      this.prevReached = pairsDone;
    }

    setBarOpen(open) {
      this.el.rb.classList.toggle('is-open', open);
      this.el.rbToggle.setAttribute('aria-expanded', String(open));
    }

    autoOpen() {
      if (this.userOpened) return;
      this.setBarOpen(true);
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (!this.userOpened) this.setBarOpen(false);
      }, AUTO_OPEN_MS);
    }

    // ---------- Slots ----------
    thumb(b, cls) {
      return b.imgS
        ? `<img class="${cls}" src="${esc(b.imgS)}" alt="" width="48" height="48" loading="lazy">`
        : `<span class="${cls} ${cls}--empty" aria-hidden="true"></span>`;
    }

    renderSlots(r) {
      const pairs = this.pairCount(r.count);
      const p = this.pct;
      let h = '';
      for (let k = 0; k < pairs; k++) {
        h += `<div class="mm-pair" role="group" aria-label="Paar ${k + 1}">`;
        for (const j of [0, 1]) {
          const i = k * 2 + j;
          const e = r.o[i];
          const disc = j === 1;
          const next = i === r.count;
          const badge = disc ? `<span class="mm-badge${e && !r.ok ? ' mm-badge--off' : ''}" aria-hidden="true">−${p} %</span>` : '';
          if (e) {
            const b = this.bowl(e.id);
            const reduced = disc && r.ok;
            const price = reduced ? `<s>${this.fmt(b.price)}</s> <b>${this.fmt(b.price - this.off(b.price))}</b>` : this.fmt(b.price);
            const label = `${b.title}, ${reduced ? `${this.fmt(b.price - this.off(b.price))} statt ${this.fmt(b.price)}` : this.fmt(b.price)}. Tauschen oder entfernen`;
            h += `<button type="button" class="mm-slot is-filled${disc ? ' mm-slot--disc' : ''}${e.uid === this.newUid ? ' is-pop' : ''}" data-mm-slot="${i}" data-uid="${e.uid}" aria-label="${esc(label)}">${badge}${this.thumb(b, 'mm-slot__img')}<span class="mm-slot__txt"><b>${esc(b.title)}</b><span class="mm-slot__pr">${price}</span></span></button>`;
          } else {
            const txt = disc ? `${p} % günstiger` : 'Bowl wählen';
            h += `<button type="button" class="mm-slot${disc ? ' mm-slot--disc' : ''}${next ? ' is-next' : ''}" data-mm-slot="${i}" aria-label="Bowl ${i + 1} wählen${disc ? `, ${p} % günstiger` : ''}">${badge}<span class="mm-slot__plus" aria-hidden="true">+</span><span class="mm-slot__txt"><small>Bowl ${i + 1}</small><b>${txt}</b></span></button>`;
          }
        }
        h += '</div>';
      }
      this.el.pairs.innerHTML = h;
      this.newUid = null;
    }

    // ---------- Sheet ----------
    sheetStatus(r) {
      const p = this.pct;
      if (!r.count) return [`Jede 2. Bowl ist ${p} % günstiger.`, false];
      if (!r.ok && r.count >= 2) return [`Noch ${this.fmt(r.minGap)} bis ${this.cfg.minLabel}, dann sparst du ${this.fmt(r.pot)}.`, false];
      if (r.count % 2) return [`${r.count} gewählt. Noch 1 Bowl bis zur nächsten −${p} %-Bowl.`, false];
      return [`${r.count} gewählt. Du sparst ${this.fmt(r.disc)}.`, true];
    }

    renderSheet(r) {
      if (!this.isSheetOpen()) return;
      const rep = this.mode.type === 'replace';
      const entry = rep ? this.seq.find((e) => e.uid === this.mode.uid) : null;
      this.el.shTitle.textContent = rep ? 'Bowl tauschen' : 'Sorten wählen';

      const [msg, good] = this.sheetStatus(r);
      this.el.shStatus.hidden = rep;
      if (this.el.shStatus.textContent !== msg) this.el.shStatus.textContent = msg;
      this.el.shStatus.classList.toggle('is-ok', good);

      const ps = this.perkState(r);
      const perkMsg = !rep && ps.near ? `Noch ${this.fmt(ps.gap)} bis ${ps.next.title}.` : '';
      this.el.shPerk.hidden = !perkMsg;
      if (this.el.shPerk.textContent !== perkMsg) this.el.shPerk.textContent = perkMsg;

      this.el.shCur.hidden = !rep;
      if (rep && entry) {
        this.el.shCur.innerHTML = `<span>Aktuell: <b>${esc(this.bowl(entry.id).title)}</b></span><button type="button" class="mm-link" data-mm-remove>Entfernen</button>`;
      }

      const counts = {};
      this.seq.forEach((e) => (counts[e.id] = (counts[e.id] || 0) + 1));
      this.el.shList.innerHTML = this.bowls
        .map((b) => {
          const id = String(b.id);
          const q = counts[id] || 0;
          const isCur = entry && String(entry.id) === id;
          let ctrl;
          if (rep) {
            const dis = isCur || !b.available;
            const txt = isCur ? 'Aktuell' : b.available ? 'Wählen' : 'Ausverkauft';
            ctrl = `<button type="button" class="mm-pick" data-mm-pick="${id}"${dis ? ' disabled' : ''} aria-label="${esc(`${b.title} ${txt.toLowerCase()}`)}">${txt}</button>`;
          } else {
            ctrl = `<div class="mm-qty"><button type="button" class="mm-qb" data-mm-dec="${id}"${q ? '' : ' disabled'} aria-label="${esc(b.title)} entfernen">−</button><span class="mm-qv" aria-label="${q} gewählt">${q}</span><button type="button" class="mm-qb mm-qb--add" data-mm-inc="${id}"${b.available ? '' : ' disabled'} aria-label="${esc(b.title)} hinzufügen">+</button></div>`;
          }
          const price = b.compare > b.price ? `<s>${this.fmt(b.compare)}</s> ${this.fmt(b.price)}` : this.fmt(b.price);
          const unit = this.unitText(b);
          const imgM = b.imgM ? `<img class="mm-row__img" src="${esc(b.imgM)}" alt="" width="56" height="56" loading="lazy">` : '<span class="mm-row__img" aria-hidden="true"></span>';
          return `<div class="mm-row${b.available ? '' : ' is-soldout'}">${imgM}<span class="mm-row__txt"><b>${esc(b.title)}</b>${b.kids ? '<span class="mm-kid">Kids</span>' : ''}<span class="mm-row__p">${price}</span><small>${b.available ? esc(unit) : 'Gerade ausverkauft'}</small></span>${ctrl}</div>`;
        })
        .join('');

      this.el.shDone.textContent = rep
        ? 'Abbrechen'
        : r.count
          ? `Fertig: ${r.count} ${r.count === 1 ? 'Bowl' : 'Bowls'} für ${this.fmt(r.total)}`
          : 'Fertig';
    }

    // ---------- Zusammenfassung + Sticky ----------
    renderSummary(r) {
      const e = this.el;
      e.cnt.textContent = `Bowls (${r.count})`;
      e.bowlSum.textContent = this.fmt(r.bowlSum);
      e.topRow.hidden = !r.topSum;
      e.topSum.textContent = this.fmt(r.topSum);
      e.disc.textContent = r.ok ? `– ${this.fmt(r.disc)}` : r.count >= 2 ? `ab ${this.cfg.minLabel}` : '–';
      e.discRow.classList.toggle('is-off', !r.disc);
      e.total.textContent = this.fmt(r.total);
      e.cartNote.hidden = !r.inCart;
      if (r.inCart) e.cartNote.textContent = `Dazu ${this.fmt(r.inCart)} schon im Warenkorb, zählt zum Mindestbestellwert.`;

      const showHint = r.count >= 2 && !r.ok;
      e.hint.hidden = !showHint;
      if (showHint) e.hint.textContent = `Noch ${this.fmt(r.minGap)} bis zum Mindestbestellwert, dann sparst du ${this.fmt(r.pot)}.`;

      this.renderPerks(r);

      this.querySelectorAll('[data-mm-add]').forEach((b) => (b.disabled = !r.count || this.busy));
      e.stCnt.innerHTML = `${r.count} ${r.count === 1 ? 'Bowl' : 'Bowls'}${r.disc ? ` <span class="mm-sticky__save">−${this.fmt(r.disc)}</span>` : ''}`;
      e.stTot.textContent = this.fmt(r.total);

      // Eingelöster Code (von herbst-promo.js oder per Link) sichtbar machen
      const codes = this.cartCodes || [];
      e.codeNote.hidden = !codes.length;
      if (codes.length) e.codeNote.textContent = `✓ Code ${codes.join(', ')} aktiviert`;
      e.stCode.hidden = !codes.length;
      if (codes.length) e.stCode.textContent = `✓ Code ${codes[0]} aktiviert`;

      this.querySelectorAll('[data-mm-top]').forEach((b) => {
        const on = this.topsOn.has(b.dataset.mmTop);
        b.setAttribute('aria-pressed', String(on));
        b.textContent = on ? b.dataset.labelOn : b.dataset.labelOff;
      });
    }

    render() {
      // Fokus im Sheet über das Neuzeichnen retten
      const a = document.activeElement;
      const key = a && this.el.overlay.contains(a) ? ['mmInc', 'mmDec', 'mmPick'].find((k) => a.dataset[k]) : null;
      const keyId = key ? a.dataset[key] : null;

      const r = this.calc();
      this.renderSlots(r);
      this.renderSummary(r);
      this.renderBar(r);
      this.renderSheet(r);

      if (key) {
        const attr = key === 'mmInc' ? 'inc' : key === 'mmDec' ? 'dec' : 'pick';
        let t = this.el.shList.querySelector(`[data-mm-${attr}="${keyId}"]`);
        if (t && t.disabled) t = this.el.shList.querySelector(`[data-mm-inc="${keyId}"]`);
        (t && !t.disabled ? t : this.el.shClose).focus();
      }
      return r;
    }

    openSheet(mode, trigger) {
      this.mode = mode;
      this.lastFocus = trigger || document.activeElement;
      this.lastSlot = trigger?.dataset.mmSlot ?? null;
      clearTimeout(this.timer);
      this.userOpened = false;
      this.setBarOpen(false);
      this.el.overlay.hidden = false;
      this.el.overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.render();
      requestAnimationFrame(() => this.el.shClose.focus());
    }

    closeSheet() {
      if (!this.isSheetOpen()) return;
      this.el.overlay.hidden = true;
      this.el.overlay.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('cart-drawer.active')) document.body.classList.remove('overflow-hidden');
      this.render();
      // Auslöser kann weg sein (Slot neu gezeichnet) oder versteckt („+ Bowl“ verschwindet bei gerader Anzahl)
      let t = this.lastFocus;
      if (!t || !t.isConnected || t.hidden || !t.getClientRects().length) {
        t = (this.lastSlot != null && this.el.pairs.querySelector(`[data-mm-slot="${this.lastSlot}"]`)) || this.el.rbToggle;
      }
      t.focus({ preventScroll: true });
    }

    // ---------- Events ----------
    onClick(e) {
      const t = e.target;
      if (t === this.el.overlay) return this.closeSheet();

      const slot = t.closest('[data-mm-slot]');
      if (slot) return this.openSheet(slot.dataset.uid ? { type: 'replace', uid: Number(slot.dataset.uid) } : { type: 'add' }, slot);
      if (t.closest('[data-mm-open]')) return this.openSheet({ type: 'add' }, t.closest('[data-mm-open]'));
      if (t.closest('[data-mm-close]')) return this.closeSheet();

      if (t.closest('[data-mm-toggle]')) {
        clearTimeout(this.timer);
        const open = !this.el.rb.classList.contains('is-open');
        this.userOpened = open;
        return this.setBarOpen(open);
      }

      const inc = t.closest('[data-mm-inc]');
      if (inc) {
        const u = ++this.uid;
        this.seq.push({ uid: u, id: inc.dataset.mmInc });
        this.newUid = u;
        return this.render();
      }
      const dec = t.closest('[data-mm-dec]');
      if (dec) {
        const id = dec.dataset.mmDec;
        for (let i = this.seq.length - 1; i >= 0; i--) {
          if (this.seq[i].id === id) {
            this.seq.splice(i, 1);
            break;
          }
        }
        return this.render();
      }
      const pick = t.closest('[data-mm-pick]');
      if (pick) {
        const en = this.seq.find((x) => x.uid === this.mode.uid);
        if (en) {
          en.id = pick.dataset.mmPick;
          this.newUid = en.uid;
        }
        return this.closeSheet();
      }
      if (t.closest('[data-mm-remove]')) {
        this.seq = this.seq.filter((x) => x.uid !== this.mode.uid);
        this.lastFocus = null;
        return this.closeSheet();
      }

      const top = t.closest('[data-mm-top]');
      if (top) {
        const id = top.dataset.mmTop;
        this.topsOn.has(id) ? this.topsOn.delete(id) : this.topsOn.add(id);
        return this.render();
      }

      const add = t.closest('[data-mm-add]');
      if (add) this.addToCart(add);
    }

    onKeydown(e) {
      if (!this.isSheetOpen()) {
        if (e.key === 'Escape' && this.el.rb.classList.contains('is-open')) {
          this.userOpened = false;
          this.setBarOpen(false);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        return this.closeSheet();
      }
      if (e.key !== 'Tab') return;
      const f = [...this.el.sheet.querySelectorAll('button:not([disabled]), a[href]')].filter((n) => n.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    onPointerdown(e) {
      if (!this.el.rb.classList.contains('is-open')) return;
      if (this.el.rb.contains(e.target) || this.el.overlay.contains(e.target)) return;
      clearTimeout(this.timer);
      this.userOpened = false;
      this.setBarOpen(false);
    }

    // ---------- Sticky-Offset unter dem Shop-Header ----------
    watchHeader() {
      const header = document.querySelector('.section-header');
      if (!header) return;
      const update = () => {
        const sticky = getComputedStyle(header).position === 'sticky' && !header.classList.contains('shopify-section-header-hidden');
        this.style.setProperty('--mm-top', `${sticky ? header.offsetHeight : 0}px`);
      };
      if ('ResizeObserver' in window) {
        this.headerRo = new ResizeObserver(update);
        this.headerRo.observe(header);
      }
      this.headerMo = new MutationObserver(update);
      this.headerMo.observe(header, { attributes: true, attributeFilter: ['class'] });
      update();
    }

    // Mobile Sticky-CTA ausblenden, sobald das Ende der Section erreicht ist (verdeckt sonst den Footer)
    watchEnd() {
      if (!this.el.end || !('IntersectionObserver' in window)) return;
      this.endIo = new IntersectionObserver(([en]) => {
        const past = en.isIntersecting || en.boundingClientRect.top < 0;
        this.el.sticky.classList.toggle('is-away', past);
      });
      this.endIo.observe(this.el.end);
    }

    // ---------- Warenkorb ----------
    // Bestehenden Warenkorb lesen: zählt zum Mindestbestellwert und zu den Gratis-Stufen
    async loadCart() {
      try {
        const cart = await fetch(`${this.cfg.cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json());
        // Automatisch hinzugefügte Geschenke (herbst-promo.js) zählen nicht zum Warenkorbwert
        const gifts = (cart.items || []).filter((l) => l.properties && l.properties._herbst_geschenk);
        this.cartGross = (cart.original_total_price || 0) - gifts.reduce((a, l) => a + l.original_line_price, 0);
        // Bestellrabatte (z. B. LoyaltyLion-Punkte) zählen wie in herbst-promo.js nicht gegen die Gratis-Stufen
        const orderDisc = (cart.cart_level_discount_applications || []).reduce((a, d) => a + (d.total_allocated_amount || 0), 0);
        this.cartNet = (cart.total_price || 0) + orderDisc - gifts.reduce((a, l) => a + l.final_line_price, 0);
        this.cartCodes = (cart.discount_codes || []).map((d) => d.code);
      } catch (err) {
        this.cartGross = 0;
        this.cartNet = 0;
        this.cartCodes = [];
      }
      this.render();
    }

    setError(msg) {
      this.querySelectorAll('[data-mm-error]').forEach((n) => {
        n.textContent = msg || '';
        n.hidden = !msg;
      });
    }

    async addToCart(btn) {
      const r = this.calc();
      if (!r.count || this.busy) return;

      // Pro Variante zusammenfassen, keine Line-Item-Properties (Discount Plus, keine doppelten Zeilen)
      const qty = new Map();
      r.o.forEach((e) => qty.set(e.id, (qty.get(e.id) || 0) + 1));
      this.topsOn.forEach((id) => qty.set(id, (qty.get(id) || 0) + 1));
      const items = [...qty].map(([id, quantity]) => ({ id: Number(id), quantity }));

      this.busy = true;
      this.setError('');
      this.querySelectorAll('[data-mm-add]').forEach((b) => {
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
      });

      try {
        const res = await fetch(`${this.cfg.addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.status) {
          this.setError(json.description || json.message || 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
          return;
        }

        // Erfolg: Auswahl zurücksetzen
        this.seq = [];
        this.topsOn.clear();
        this.prevReached = 0;
        this.lastNudged = null;

        await this.loadCart();
        const drawer = document.querySelector('cart-drawer');
        if (this.cfg.afterAdd === 'drawer' && drawer) {
          await this.openDrawer(drawer, btn);
        } else {
          window.location.href = this.cfg.cartUrl;
          return;
        }
      } catch (err) {
        console.error('[mix-match-sale]', err);
        this.setError('Das hat nicht geklappt. Bitte versuch es noch einmal.');
      } finally {
        this.busy = false;
        this.querySelectorAll('[data-mm-add]').forEach((b) => b.removeAttribute('aria-busy'));
        this.render();
      }
    }

    // Multi-Item-Add liefert kein `id`, das Dawns renderContents() erwartet → Sections selbst holen
    async openDrawer(drawer, trigger) {
      const res = await fetch(`${this.cfg.rootUrl.replace(/\/$/, '')}/?sections=cart-drawer,cart-icon-bubble`);
      if (!res.ok) {
        window.location.href = this.cfg.cartUrl;
        return;
      }
      const sections = await res.json();
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

      const drawerInner = document.getElementById('CartDrawer');
      const newDrawer = sections['cart-drawer'] && parse(sections['cart-drawer']).querySelector('#CartDrawer');
      if (drawerInner && newDrawer) drawerInner.innerHTML = newDrawer.innerHTML;

      const bubble = document.getElementById('cart-icon-bubble');
      const newBubble = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;

      drawer.classList.remove('is-empty');
      drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
      // Overlay wurde mit ersetzt → Listener wie in Dawns renderContents() neu binden
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));

      // Kein cartUpdate-Event: Der angepasste CartDrawerItems.onCartUpdate lädt darauf das Markup der
      // Warenkorbseite in den Drawer, dabei gehen die Drawer-Stile verloren (Rabattblock + Riesen-Icon).
      // Herbst-Promo und andere Listener erfahren die Änderung über ihren eigenen fetch-/XHR-Abgleich.
      setTimeout(() => drawer.open(trigger));
    }
  }

  customElements.define('mix-match-sale', MixMatchSale);
})();
